/**
 * バイアス分析パネルコンポーネント
 *
 * 面接のバイアス分析結果を表示・可視化
 * 科学的根拠: Wilson et al. (2025), MDPI AI (2024)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  LinearProgress,
  Tooltip,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Science as ScienceIcon,
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  Gavel as GavelIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { bg } from '../../../../utils/darkModeAdapter';
import { biasService } from '../../services/audioAPI';
import BiasScoreGauge from './BiasScoreGauge';
import HaloEffectChart from './HaloEffectChart';

/**
 * バイアス分析パネル
 */
export default function BiasAnalysisPanel({
  checkSheetId,
  questions = [],
  evaluations = [],
  onAnalysisComplete = null
}) {
  const theme = useTheme();
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // 既存の分析結果を読み込み
  useEffect(() => {
    if (checkSheetId) {
      loadExistingAnalysis();
    }
  }, [checkSheetId]);

  const loadExistingAnalysis = async () => {
    setIsLoading(true);
    try {
      const result = await biasService.getBiasAnalysis(checkSheetId);
      setAnalysis(result);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to load bias analysis:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // バイアス分析を実行
  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const analysisData = {
        analysis_type: 'overall',
        questions: questions,
        evaluations: evaluations
      };

      const result = await biasService.analyzeBias(checkSheetId, analysisData);
      setAnalysis(result);

      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    } catch (err) {
      setError('バイアス分析に失敗しました');
      console.error('Bias analysis failed:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 深刻度に応じたアイコンを取得
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  // 深刻度に応じた色を取得
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // バイアスタイプの日本語名
  const biasTypeLabels = {
    gender_bias: '性別バイアス',
    age_bias: '年齢バイアス',
    halo_effect: 'ハロー効果',
    confirmation_bias: '確証バイアス',
    similarity_bias: '類似性バイアス',
    leniency_severity: '寛大化/厳格化傾向'
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PsychologyIcon color="primary" />
          バイアス検出分析
        </Typography>
        <Button
          variant="outlined"
          startIcon={isAnalyzing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={runAnalysis}
          disabled={isAnalyzing}
        >
          {analysis ? '再分析' : '分析実行'}
        </Button>
      </Box>

      {/* 科学的根拠 */}
      <Alert severity="info" icon={<ScienceIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>科学的根拠:</strong> AIバイアスは人間の採用判断に伝播することが実証されています（Wilson et al., 2025）。
          本機能は質問内容と評価パターンから無意識バイアスを検出し、公平な採用判断を支援します。
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAnalyzing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            バイアス分析中...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {!analysis && !isAnalyzing && (
        <Alert severity="warning">
          バイアス分析がまだ実行されていません。「分析実行」ボタンをクリックして分析を開始してください。
        </Alert>
      )}

      {analysis && (
        <>
          {/* 総合スコア */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <BiasScoreGauge
                score={analysis.overall_bias_score}
                label="総合バイアススコア"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, height: '100%', backgroundColor: bg(theme).subtle }}>
                <Typography variant="subtitle2" gutterBottom>
                  分析サマリー
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analysis.overall_bias_score <= 20 && (
                    <>
                      <CheckCircleIcon color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      バイアスの兆候はほとんど検出されませんでした。公平な評価が行われています。
                    </>
                  )}
                  {analysis.overall_bias_score > 20 && analysis.overall_bias_score <= 50 && (
                    <>
                      <WarningIcon color="warning" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      軽度のバイアス傾向が検出されました。以下の提案を参考に改善を検討してください。
                    </>
                  )}
                  {analysis.overall_bias_score > 50 && (
                    <>
                      <ErrorIcon color="error" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      顕著なバイアス傾向が検出されました。評価の見直しを強く推奨します。
                    </>
                  )}
                </Typography>

                {/* カテゴリ別スコア */}
                {analysis.category_bias_scores && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      カテゴリ別スコア:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {Object.entries(analysis.category_bias_scores).map(([category, score]) => (
                        <Chip
                          key={category}
                          label={`${biasTypeLabels[category] || category}: ${score}`}
                          size="small"
                          color={score > 50 ? 'error' : score > 20 ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* 検出されたバイアス一覧 */}
          {analysis.detected_biases && analysis.detected_biases.length > 0 && (
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  検出されたバイアス ({analysis.detected_biases.length}件)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {analysis.detected_biases.map((bias, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start">
                        <ListItemIcon>
                          {getSeverityIcon(bias.severity)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {biasTypeLabels[bias.bias_type] || bias.bias_type}
                              </Typography>
                              <Chip
                                label={bias.severity === 'high' ? '高' : bias.severity === 'medium' ? '中' : '低'}
                                size="small"
                                color={getSeverityColor(bias.severity)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {bias.description}
                              </Typography>
                              {bias.detected_text && (
                                <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                  <Typography variant="caption">
                                    検出箇所: "{bias.detected_text}"
                                  </Typography>
                                </Alert>
                              )}
                              {bias.recommendation && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>推奨対応:</strong> {bias.recommendation}
                                </Typography>
                              )}
                              {bias.legal_reference && (
                                <Typography variant="caption" color="text.secondary">
                                  <GavelIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {bias.legal_reference}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < analysis.detected_biases.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* ハロー効果分析 */}
          {analysis.halo_effect_analysis && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="info" />
                  ハロー効果分析
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <HaloEffectChart data={analysis.halo_effect_analysis} />
              </AccordionDetails>
            </Accordion>
          )}

          {/* 評価一貫性分析 */}
          {analysis.evaluation_consistency && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="info" />
                  評価一貫性分析
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  評価項目間の相関係数: {analysis.evaluation_consistency.correlation_coefficient?.toFixed(2) || 'N/A'}
                </Typography>
                {analysis.evaluation_consistency.inconsistent_items?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    以下の項目で評価の不一致が検出されました:
                    <ul>
                      {analysis.evaluation_consistency.inconsistent_items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* 改善提案 */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  改善提案 ({analysis.recommendations.length}件)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {analysis.recommendations.map((rec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* 科学的参照 */}
          {analysis.scientific_references && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                <ScienceIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                科学的参照: {analysis.scientific_references.join(', ')}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
